"use client";

import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import Image from "../../../components/common/Image";
import { FaDesktop, FaMobile, FaShoppingCart, FaSearch, FaLaptopCode, FaTools } from "react-icons/fa";
import config from '../../../config/services.json';
import { NameWithLogo } from "../../../types";
import HeroSection from "../../../components/common/HeroSection";

// Animation variants
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

export default function WebDevelopmentPage() {
  const { technologies, developmentProcess } = config;
  return (
    <main className="min-h-screen bg-gradient-to-br from-white via-gray-50 to-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <HeroSection
          title="Web Development"
          description="Creating powerful, responsive, and user-friendly web experiences that drive results"
          showPattern={true}
        />
      </section>

      {/* Services Overview */}
      <section className="pb-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              className="space-y-4 md:space-y-6"
            >
              <h2 className="text-2xl md:text-3xl font-bold">Modern Web Solutions for Your Business</h2>
              <p className="text-base md:text-lg text-gray-600">
                We create custom web applications and websites that are fast, secure, and scalable. 
                Our development team uses the latest technologies to deliver solutions that meet your 
                business needs and exceed user expectations.
              </p>
              <div className="space-y-3 md:space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center mt-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-gray-700">Responsive designs that work on all devices</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center mt-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-gray-700">SEO-friendly architecture for better visibility</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center mt-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-gray-700">Fast loading times for better user experience</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center mt-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-gray-700">Secure and scalable infrastructure</p>
                </div>
              </div>
            </motion.div>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              className="relative mt-8 md:mt-0"
            >
              <div className="relative h-[300px] md:h-[400px] w-full rounded-3xl overflow-hidden shadow-2xl">
                <Image
                  src="/images/services/responsive.png"
                  alt="Web Development"
                  fill
                  className="object-cover p-6 md:p-12"
                />
              </div>
              <div className="absolute -bottom-4 md:-bottom-6 -left-4 md:-left-6 w-24 md:w-32 h-24 md:h-32 bg-indigo-600 rounded-full opacity-20"></div>
              <div className="absolute -top-4 md:-top-6 -right-4 md:-right-6 w-24 md:w-32 h-24 md:h-32 bg-purple-600 rounded-full opacity-20"></div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <h2 className="text-3xl font-bold mb-6">Our Web Development Services</h2>
            <p className="text-lg text-gray-600">
              We offer a comprehensive range of web development services to help your business succeed online.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: <FaDesktop className="w-6 h-6" />,
                title: "Website Design & Development",
                description: "Custom websites that are visually appealing, functional, and aligned with your brand identity."
              },
              {
                icon: <FaLaptopCode className="w-6 h-6" />,
                title: "Web Application Development",
                description: "Scalable and secure web applications with intuitive interfaces and powerful functionality."
              },
              {
                icon: <FaShoppingCart className="w-6 h-6" />,
                title: "E-commerce Solutions",
                description: "Online stores with secure payment gateways, inventory management, and seamless user experience."
              },
              {
                icon: <FaMobile className="w-6 h-6" />,
                title: "Responsive Web Design",
                description: "Websites that adapt perfectly to all screen sizes and devices for optimal user experience."
              },
              {
                icon: <FaSearch className="w-6 h-6" />,
                title: "SEO Integration",
                description: "Built-in search engine optimization to improve your website's visibility and ranking."
              },
              {
                icon: <FaTools className="w-6 h-6" />,
                title: "Website Maintenance",
                description: "Regular updates, security patches, and performance optimization to keep your website running smoothly."
              }
            ].map((service, index) => (
              <motion.div
                key={service.title}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeIn}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -10 }}
                className="bg-gradient-to-br from-white to-gray-50 p-8 rounded-3xl shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-r from-indigo-600 to-purple-600 
                  text-white flex items-center justify-center mb-6">
                  {service.icon}
                </div>
                <h3 className="text-xl font-bold mb-4">{service.title}</h3>
                <p className="text-gray-600">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="pt-20 pb-60">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <h2 className="text-2xl md:text-3xl font-bold mb-6">Our Development Process</h2>
            <p className="text-base md:text-lg text-gray-600">
              We follow a structured approach to deliver high-quality web solutions that meet your business objectives.
            </p>
          </motion.div>

          <div className="relative max-w-5xl mx-auto h-auto md:h-[500px] perspective-[2000px]">
            { developmentProcess.map((process, index) => (
              <motion.div
                key={process.step}
                className="block mb-6 md:mb-4 md:absolute md:inset-x-0"
                style={{
                  top: `${index * 140}px`,
                  transformStyle: "preserve-3d"
                }}
                initial={{ opacity: 0, rotateX: 45, y: 100 }}
                whileInView={{
                  opacity: 1,
                  rotateX: 0,
                  y: 0
                }}
                viewport={{ once: true }}
                transition={{
                  duration: 0.8,
                  delay: index * 0.2,
                  ease: "easeOut"
                }}
                whileHover={{
                  scale: 1.02,
                  rotateX: -5,
                  transition: { duration: 0.3 }
                }}
              >
                <div className={`bg-gradient-to-r ${process.color} p-6 md:p-8 rounded-3xl shadow-2xl 
                  text-white relative overflow-hidden group`}>
                  <div className="absolute -right-8 -top-8 w-24 md:w-32 h-24 md:h-32 bg-white/10 rounded-full blur-2xl 
                    group-hover:scale-150 transition-transform duration-500" />
                  
                  <div className="flex flex-col md:flex-row items-start gap-4 md:gap-6">
                    <div className="flex-shrink-0 w-12 h-12 md:w-16 md:h-16 bg-white/20 rounded-2xl flex items-center 
                      justify-center text-2xl md:text-3xl backdrop-blur-sm">
                      {process.icon}
                    </div>
                    <div className="space-y-2 md:space-y-4">
                      <div className="flex items-center gap-2 md:gap-3">
                        <span className="text-3xl md:text-5xl font-bold opacity-50">{process.step}</span>
                        <h3 className="text-xl md:text-2xl font-bold">{process.title}</h3>
                      </div>
                      <p className="text-base md:text-lg text-white/80">{process.description}</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies */}
      <section className="py-20 bg-white overflow-hidden">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <h2 className="text-2xl md:text-3xl font-bold mb-6">Technologies We Use</h2>
            <p className="text-base md:text-lg text-gray-600">
              We leverage the latest technologies to build modern, scalable, and high-performance web solutions.
            </p>
          </motion.div>

          <div className="relative h-[300px] md:h-[400px] perspective-[2000px] mx-auto max-w-6xl overflow-hidden">
            <div className="absolute inset-y-0 left-0 w-16 md:w-32 bg-gradient-to-r from-white to-transparent z-10" />
            <div className="absolute inset-y-0 right-0 w-16 md:w-32 bg-gradient-to-l from-white to-transparent z-10" />
            
            <motion.div
              className="absolute inset-0"
              animate={{
                rotateY: [20, -20]
              }}
              transition={{
                duration: 10,
                repeat: Infinity,
                repeatType: "reverse",
                ease: "easeInOut"
              }}
              style={{ transformStyle: "preserve-3d" }}
            >
              <motion.div 
                className="flex gap-4 md:gap-8 items-center h-full"
                animate={{
                  x: [0, -1200]
                }}
                transition={{
                  duration: 20,
                  repeat: Infinity,
                  repeatType: "loop",
                  ease: "linear"
                }}
              >
                {[...Array(3)].map((_, i) => (
                  <div key={`row-${i}`} className="flex gap-4 md:gap-8 flex-nowrap">
                    {technologies.webDevelopment.map((tech: NameWithLogo, index: number) => (
                      <motion.div
                        key={`${tech.name}-${i}-${index}`}
                        className="flex-shrink-0 w-36 h-36 md:w-48 md:h-48"
                        style={{
                          transform: `translateZ(${Math.sin(index * 0.5) * 100}px)`
                        }}
                        whileHover={{
                          scale: 1.1,
                          z: 50,
                          transition: { duration: 0.3 }
                        }}
                      >
                        <div className="w-full h-full bg-gradient-to-br from-white to-gray-50 
                          rounded-2xl shadow-lg p-6 md:p-8 flex flex-col items-center justify-center
                          hover:shadow-2xl transition-all duration-300 relative group">
                          <div className="relative h-16 w-16 md:h-24 md:w-24 mb-3 md:mb-4 flex justify-center items-center">
                            <Image
                              src={tech.logo}
                              alt={tech.name}
                              className="object-contain h-auto w-full"
                            />
                          </div>
                          <p className="font-medium text-sm md:text-base text-gray-800 text-center group-hover:text-indigo-600 
                            transition-colors duration-300">{tech.name}</p>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h2
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              className="text-3xl md:text-4xl font-bold mb-6"
            >
              Ready to Start Your Web Development Project?
            </motion.h2>
            <motion.p
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              transition={{ delay: 0.1 }}
              className="text-lg text-indigo-100 mb-10"
            >
              Let's discuss how we can help you achieve your business goals with a custom web solution.
            </motion.p>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              transition={{ delay: 0.2 }}
            >
              <Link
                to="/contact"
                className="inline-flex items-center px-8 py-4 rounded-full bg-white text-indigo-600 
                  hover:shadow-lg hover:shadow-indigo-700/20 transition-all duration-300 text-lg font-semibold"
              >
                Get a Free Consultation
                <svg className="w-5 h-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials Section - NEW */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <h2 className="text-3xl font-bold mb-6">What Our Clients Say</h2>
            <p className="text-lg text-gray-600">
              Don't just take our word for it. Here's what our clients have to say about our web development services.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                name: "Sarah Johnson",
                company: "TechStart Inc.",
                image: "/images/testimonials/client1.jpg",
                quote: "ASCIIBytes transformed our outdated website into a modern, user-friendly platform that has significantly increased our conversion rates."
              },
              {
                name: "Michael Chen",
                company: "Global Solutions",
                image: "/images/testimonials/client2.jpg",
                quote: "The e-commerce solution developed by ASCIIBytes has streamlined our online sales process and improved our customer experience dramatically."
              },
              {
                name: "Emily Rodriguez",
                company: "Creative Studios",
                image: "/images/testimonials/client3.jpg",
                quote: "Working with ASCIIBytes was a pleasure from start to finish. They delivered our web application on time and exceeded our expectations."
              }
            ].map((testimonial, index) => (
              <motion.div
                key={testimonial.name}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeIn}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-8 rounded-3xl shadow-lg relative"
              >
                <div className="flex items-center mb-6">
                  {/* <div className="w-14 h-14 rounded-full overflow-hidden mr-4">
                    <Image
                      src={testimonial.image}
                      alt={testimonial.name}
                      width={56}
                      height={56}
                      className="object-cover w-full h-full"
                    />
                  </div> */}
                  <div>
                    <h3 className="font-bold text-lg">{testimonial.name}</h3>
                    <p className="text-gray-600 text-sm">{testimonial.company}</p>
                  </div>
                </div>
                <p className="text-gray-700 italic">"{testimonial.quote}"</p>
                <div className="absolute -top-3 -right-3 text-5xl text-indigo-200">"</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}